import { DataContextProvider } from "contexts/DataContext";
import { getTranslations } from "contexts/LocalizationContext/utils";
import { isMagnitudeTemplate, MagnitudePage } from "modules/magnitude/templates/MagnitudePage";
import { StoryblokTemplate } from "modules/storyblok/templates/StoryblokTemplate";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";
import { getCoralCampaign } from "services/coral";
import type { PageProps } from "types/global";
import type { ServerSideParams } from "types/storyblok";
import { getNofollowUrls } from "utils/getNofollowUrls";
import { checkIfValidLanguage } from "utils/localeUtils";
import {
  getAntiFlickerSettings,
  getGoogleOptimizeExperimentIds,
  getWhiteListDomains,
} from "utils/serverSideUtils/datasources";
import { getDataFromCMS } from "utils/serverSideUtils/getDataFromCMS";
import { getServerSideStoryblokPages } from "utils/serverSideUtils/getServerSideStoryblokPages";
import { isPageTrackingless } from "utils/tracking/utils";

const Page = (props: PageProps) => {
  const { serverSideStoryblokPages } = props;

  return (
    <DataContextProvider data={{ serverSideStoryblokPages }}>
      {isMagnitudeTemplate(props) ? <MagnitudePage {...props} /> : <StoryblokTemplate {...props} />}
    </DataContextProvider>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { query, req: { headers: { referer: pageReferrer = "" } } } = context;
  const { language } = query as ServerSideParams;

  if (!checkIfValidLanguage(language)) {
    return {
      notFound: true,
    };
  }

  // TODO | Add types after migration getTranslations, getDataFromCMS, etc to TS
  const [
    translations,
    cmsResponse,
    antiFlickerSettings,
    serverSideStoryblokPages,
    whiteListDomains,
    googleOptimizeExperimentIds,
  ] = await Promise.all<any>([
    getTranslations(language, []),
    getDataFromCMS(context),
    getAntiFlickerSettings(),
    getServerSideStoryblokPages(),
    getWhiteListDomains(),
    getGoogleOptimizeExperimentIds(),
  ]);

  if (cmsResponse.errorCode === 404) {
    return {
      notFound: true,
    };
  }

  let coralCampaign = null;

  if (cmsResponse.story?.content.activeCampaign) {
    coralCampaign = await getCoralCampaign(cmsResponse.story?.content.activeCampaign);
  }

  // Default values for props from SB. We use them on pages which were create before we add some of that new configurations because currently they have values 'undefined'
  // TODO After complete migration to SB we need to check every page which uses these props and assign new values then we can remove default values

  const settings = {
    hideFlagBox: cmsResponse.story?.content?.languageSwitcher || false,
    showStickyBottomBar: cmsResponse.story?.content?.stickyBottomBar || false,
    activeButtonOption: cmsResponse.story?.content?.activeButtonOption || "check-compensation",
  };

  const isTrackingless = isPageTrackingless(query);
  const nofollowUrls = getNofollowUrls(await serverSideStoryblokPages);

  return {
    props: {
      isTrackingless,
      translations,
      language,
      antiFlickerSettings,
      serverSideStoryblokPages,
      settings,
      whiteListDomains,
      googleOptimizeExperimentIds,
      nofollowUrls,
      coralCampaign,
      pageReferrer,
      ...cmsResponse,
    },
  };
};
